import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, Tabs } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { CallButton } from '@/phone/CallButton';
import { dateTime, money } from '@/lib/format';
import { addCompanyNote, getCompany, listDeals, updateCompany } from '@/data/client';
import type { Lifecycle } from '@/data/types';
import { LIFECYCLE_OPTIONS } from '@/components/crm/LifecycleBadge';
import { CompanyFormModal } from '@/components/crm/CompanyForm';
import { ContactFormModal } from '@/components/crm/ContactForm';
import { DealFormModal } from '@/components/crm/DealForm';
import { NoteComposer, Timeline } from '@/components/crm/Timeline';
import { MeetingsPanel } from '@/components/crm/MeetingsPanel';
import { ClientVault } from '@/components/vault/ClientVault';
import { SnapshotCard } from '@/components/crm/SnapshotCard';

export function CompanyDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['company', id], queryFn: () => getCompany(id) });
  const { data: deals = [] } = useQuery({ queryKey: ['deals', { companyId: id }], queryFn: () => listDeals({ companyId: id }) });
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'vault' ? 'vault' : 'overview';
  const [editing, setEditing] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [addingDeal, setAddingDeal] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['company', id] });
    void qc.invalidateQueries({ queryKey: ['companies'] });
    void qc.invalidateQueries({ queryKey: ['snapshot', id] }); // the snapshot checks whether to update
  };
  const setLifecycle = useMutation({ mutationFn: (lifecycle: Lifecycle) => updateCompany(id, { lifecycle }), onSuccess: refresh });

  if (isLoading) return null;
  if (!data) return <div className="dash-empty"><h2>Company not found</h2><Link to="/companies">Back to companies</Link></div>;
  const { company, contacts, timeline } = data;

  return (
    <div className="crm-record">
      <Link to="/companies" className="cq-back"><Icon name="arrow-left" size={16} /> Companies</Link>
      <div className="crm-record__head">
        <div className="crm-record__title">
          <h2>
            {company.name}
            <select
              className="crm-inline-select"
              aria-label="Lifecycle"
              value={company.lifecycle}
              onChange={(e) => setLifecycle.mutate(e.target.value as Lifecycle)}
            >
              {LIFECYCLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </h2>
          <div className="crm-record__sub">
            {company.website && <a href={/^https?:/.test(company.website) ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer">{company.website}</a>}
            {company.phone && <CallButton to={company.phone} name={company.name} />}
            <span>Owner: {company.ownerName ?? 'Unassigned'}</span>
          </div>
        </div>
        <div className="crm-record__actions">
          <Button variant="outline" size="sm" iconLeft={<Icon name="edit" size={14} />} onClick={() => setEditing(true)}>Edit</Button>
        </div>
      </div>

      <div className="lead-tabsbar">
        <Tabs items={[{ value: 'overview', label: 'Overview' }, { value: 'vault', label: 'Client Vault' }]} value={tab}
          onChange={(v) => setParams(v === 'vault' ? { tab: 'vault' } : {}, { replace: true })} />
      </div>

      {tab === 'vault' ? <ClientVault companyId={company.id} companyName={company.name} /> : (
      <>
      <SnapshotCard companyId={company.id} onOpenVault={() => setParams({ tab: 'vault' }, { replace: true })} />
      <div className="crm-record__cols">
        <Card padding={false} className="crm-section">
          <div className="crm-section__head"><h3>Timeline</h3></div>
          <NoteComposer onSave={(body) => addCompanyNote(id, body).then(refresh)} />
          <Timeline items={timeline} showContact filterable />
        </Card>

        <div className="crm-record__side">
          <MeetingsPanel filter={{ companyId: company.id }} companyId={company.id} defaultTitle={`${company.name} — call`}
            guests={contacts.map((p) => ({ contactId: p.id, name: p.name ?? p.email ?? p.phone ?? 'Contact', email: p.email }))} />

          <Card padding={false} className="crm-section">
            <div className="crm-section__head">
              <h3>Contacts</h3>
              <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setAddingContact(true)}>Add</Button>
            </div>
            {contacts.length ? (
              <ul className="crm-people">
                {contacts.map((p) => (
                  <li key={p.id}>
                    <div>
                      <Link to={`/contacts/${p.id}`}>{p.name ?? p.email ?? p.phone}</Link>
                      <span className="crm-cell-sub">{[p.title, p.email].filter(Boolean).join(' · ')}</span>
                    </div>
                    {p.isPrimary && <span className="crm-cell-sub">Primary</span>}
                  </li>
                ))}
              </ul>
            ) : <p className="crm-cell-muted">No contacts yet.</p>}
          </Card>

          <Card padding={false} className="crm-section">
            <div className="crm-section__head">
              <h3>Deals</h3>
              <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setAddingDeal(true)}>New</Button>
            </div>
            {deals.length ? (
              <ul className="crm-people">
                {deals.map((d) => (
                  <li key={d.id}>
                    <div>
                      <Link to={`/deals/${d.id}`}>{d.name}</Link>
                      <span className="crm-cell-sub">{d.stageName}{d.leadCount > 1 ? ` · ${d.leadCount} leads` : ''}</span>
                    </div>
                    <span className="crm-cell-sub">{d.mrr != null ? `${money(d.mrr)}/mo` : ''}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="crm-cell-muted">No deals yet.</p>}
          </Card>

          <Card padding={false} className="crm-section">
            <div className="crm-section__head"><h3>Details</h3></div>
            <dl className="crm-fields">
              <dt>Domain</dt><dd>{company.domain ?? '—'}</dd>
              <dt>Industry</dt><dd>{company.industry ?? '—'}</dd>
              <dt>Address</dt><dd>{company.address ?? ([company.city, company.state].filter(Boolean).join(', ') || '—')}</dd>
              {company.services && <><dt>Services</dt><dd className="crm-pre">{company.services}</dd></>}
              {company.serviceAreas && <><dt>Service areas</dt><dd className="crm-pre">{company.serviceAreas}</dd></>}
              {company.tritoniqClientId && <><dt>tritonIQ</dt><dd>Linked client</dd></>}
              <dt>Created</dt><dd>{dateTime(company.createdAt)}</dd>
            </dl>
          </Card>
        </div>
      </div>

      </>
      )}

      {editing && <CompanyFormModal company={company} onClose={() => setEditing(false)} />}
      {addingDeal && <DealFormModal companyId={company.id} onClose={() => setAddingDeal(false)} />}
      {addingContact && <ContactFormModal companyId={company.id} stay onClose={() => setAddingContact(false)} />}
    </div>
  );
}
